import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { uniqueId } from 'lodash';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { filesize } from 'filesize';
import { useNavigate } from 'react-router-dom';

import api from 'services/api';
import { updateDados } from 'store/modules/usuario/actions';
import { show } from 'store/modules/dialog/actions';
import { IRootState } from 'store/modules/rootReducer';
import AppError from 'utils/AppError';
import { usePermit } from 'utils/hooks/usePermit';
import getValidationErrors from 'services/getValidationErros';
import { Permissao } from 'utils/variables';

import Input from 'components/Input';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Widget from 'components/Widget';

import {
  FiUpload,
  FiTrash2,
  FiX,
  FiMusic,
  FiBookOpen,
  FiVideo,
} from 'react-icons/fi';

import {
  Container,
  CardAvatar,
  AvatarContent,
  Avatar,
  Upload,
  ButtonUpload,
  ButtonUploadError,
  ButtonRemove,
  Info,
  DropContainer,
  CincularContainer,
  Content,
  Form,
  WidgetContent,
} from './styles';

// configuração do validator
const schema = Yup.object().shape({
  // nome: Yup.string().required('Campo Obrigatório'),
  // .min(6, 'A senha precisa de 6 caracteres')
  senha: Yup.string().required('Campo obrigatório'),
  novasenha: Yup.string().when('senha', (senha, passSchema) =>
    !senha
      ? passSchema
      : passSchema
          .min(6, 'A nova senha deve ter no mínimo 6 caracteres')
          .max(15, 'A nova senha deve ter no máximo 15 caracteres')
          .required('Campo obrigatório'),
  ),
  confirmasenha: Yup.string().oneOf(
    [Yup.ref('novasenha')],
    'As senhas não conferem',
  ),
});

export default function Perfil() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   *  REDUX
   */
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  const { profile } = useSelector((state: IRootState) => state.usuario) as any;
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);
  const dispatch = useDispatch();

  /**
   * VARIAVEIS
   */
  const [selectedPicture, setSelectedPicture] = useState<any[]>([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState({
    chamadas: 0,
    roteiropromo: 0,
    spotradio: 0,
  });
  const [initialData] = useState({
    nome: profile.nome,
  });

  /**
   * REFERENCIAS
   */
  const formRef = useRef<FormHandles>(null);

  /*
   * FUNÇÕES
   */
  function renderDragButton(isDragActive: boolean, isDragReject: boolean) {
    if (!isDragActive) {
      return (
        <>
          <ButtonUpload>
            <FiUpload />
          </ButtonUpload>
        </>
      );
    }

    if (isDragReject) {
      return (
        <ButtonUploadError>
          <FiX />
        </ButtonUploadError>
      );
    }

    return (
      <ButtonUpload>
        <FiUpload />
      </ButtonUpload>
    );
  }

  const handleDeletePicture = useCallback(() => {
    // setSelected(usu)
    const optionBase = {
      type: 'question',
      text: `Deseja remover sua foto de perfil?`,
      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/avatar`,
        method: api.delete,
      },
      haveCallback: true,
      successMessage: 'Foto removida!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };
    dispatch(show(optionBase));
  }, [dispatch]);

  const handleUpdateProfile = useCallback(
    async data => {
      try {
        setLoadingSubmit(true);
        if (formRef.current) formRef.current.setErrors({});
        await schema.validate({ ...data }, { abortEarly: false });

        const response = await api.put('/perfil', data);
        dispatch(updateDados(response.data));
        setLoadingSubmit(false);
        toast.success('Dados atualizados');
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [dispatch],
  );

  const handleUpload = useCallback((files: any[]) => {
    const filesFormatted = files.map(file => ({
      ...file,
      file,
      id: uniqueId(),
      name: file.name,
      readableSize: filesize(file.size),
      progress: 0,
      uploaded: false,
      error: false,
    }));
    setSelectedPicture(filesFormatted);
  }, []);

  /**
   * USECALLBACKS
   */

  const updateFile = useCallback(
    (id, data) => {
      const files = selectedPicture.map(uploadedFile => {
        return id === uploadedFile.id
          ? { ...uploadedFile, ...data }
          : uploadedFile;
      });
      setSelectedPicture(files);
    },
    [selectedPicture],
  );

  const processUpload = useCallback(
    selectedFile => {
      const data = new FormData();
      setLoadingSubmit(true);
      data.append('file', selectedFile.file);

      api
        .put(`/avatar`, data, {
          timeout: 10000,
          onUploadProgress: e => {
            const progress = e.total
              ? Math.round((e.loaded * 100) / e.total)
              : 0;
            updateFile(selectedFile.id, {
              progress,
            });
          },
        })
        .then(response => {
          updateFile(selectedFile.id, {
            uploaded: true,
          });
          setTimeout(() => setSelectedPicture([]), 2000);
          dispatch(updateDados(response.data));
          setLoadingSubmit(false);
          toast.success('Foto de perfil atualizada');
        })
        .catch(err => {
          toast.error(err);
          updateFile(selectedFile.id, {
            error: true,
          });
          setTimeout(() => setSelectedPicture([]), 2000);
          setLoadingSubmit(false);
        });
    },
    [dispatch, updateFile],
  );

  /**
   * USE MEMO
   */
  // const imageProfile = useMemo(
  //   () =>
  //     !!selectedPicture.length && selectedPicture[0].progress > 0
  //       ? selectedPicture[0].preview
  //       : profile.avatar_url,
  //   [profile.avatar_url, selectedPicture]
  // )

  const inUpload = useMemo(
    () =>
      !!selectedPicture.length &&
      !selectedPicture[0].uploaded &&
      !selectedPicture[0].error &&
      selectedPicture[0].progress > 10,
    [selectedPicture],
  );

  /*
   * USEEFFECT
   */

  useEffect(
    () =>
      selectedPicture.forEach(picture => {
        if (!picture.uploaded && !picture.error && picture.progress === 0) {
          processUpload(picture);
        }
      }),
    [processUpload, selectedPicture],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadPefil() {
      if (hasAvaliador) {
        try {
          const response = await api.get(`/perfil`, {
            signal: controller.signal,
          });
          if (response && response.data)
            setWorkflow({
              chamadas: response.data.chamadas || 0,
              spotradio: response.data.spotradio || 0,
              roteiropromo: response.data.roteiropromo || 0,
            });
          else toast.error('Falha de comunicação com o sistema');
        } catch (e) {
          if (e instanceof Error) toast.error(e.message);
          else if (e instanceof AppError) toast.error(e.message);
          else {
            const appError = e as AppError;
            if (appError.message) toast.error(appError.message);
            else toast.error('Erro inesperado');
          }
        }
      }
    }
    setLoading(true);
    loadPefil();
    setLoading(false);
    return () => {
      controller.abort();
    };
  }, []);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (
        callbackDialog.success.action === 'success' &&
        callbackDialog.success.data.message === 'Foto de perfil removida!'
      )
        dispatch(updateDados({ nome: profile.nome, avatar_url: 'null' }));
  }, [callbackDialog, dispatch, profile.nome]);

  return (
    <Container>
      <CardAvatar>
        <AvatarContent>
          <header>
            <div />
            <div />
            <div />
            <div />
          </header>
          <Upload>
            <Dropzone
              accept={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpeg'],
              }}
              multiple={false}
              onDropAccepted={handleUpload}
              onDropRejected={() => toast.error('Arquivo não permitido')}
            >
              {({
                getRootProps,
                getInputProps,
                isDragActive,
                isDragReject,
              }) => (
                <DropContainer
                  {...getRootProps()}
                  $isDragActive={isDragActive}
                  $isDragReject={isDragReject}
                >
                  <input {...getInputProps()} />
                  <Avatar
                    $inUpload={inUpload}
                    size="124"
                    name={profile.nome}
                    src={profile.avatar_url}
                    round
                  />
                  {renderDragButton(isDragActive, isDragReject)}
                </DropContainer>
              )}
            </Dropzone>

            <ButtonRemove onClick={handleDeletePicture}>
              <FiTrash2 />
            </ButtonRemove>

            {!!selectedPicture.length &&
              !selectedPicture[0].uploaded &&
              !selectedPicture[0].error && (
                <CincularContainer
                  styles={{
                    root: { width: 124 },
                    path: { stroke: '#3358f4' },
                  }}
                  strokeWidth={3}
                  value={selectedPicture[0].progress}
                />
              )}
          </Upload>
          <Info>
            <h5>{profile.nome}</h5>

            <span>
              <p>{profile.email}</p> / <strong>{profile.role}</strong>
            </span>
          </Info>
        </AvatarContent>
        <Content>
          <section>
            <p>DADOS PESSOAIS:</p>
          </section>
          <Form
            ref={formRef}
            initialData={initialData}
            onSubmit={handleUpdateProfile}
          >
            <div>
              {/* <Input
                name="nome"
                placeholder="Nome completo"
                disabled={loadingSubmit}
                icon=""
                errorField=""
              /> */}
              <Input
                name="senha"
                placeholder="Senha atual"
                type="password"
                disabled={loadingSubmit}
              />
              <Input
                name="novasenha"
                placeholder="Nova senha"
                type="password"
                disabled={loadingSubmit}
              />
              <Input
                name="confirmasenha"
                placeholder="Confirme nova senha"
                type="password"
                disabled={loadingSubmit}
              />
            </div>
            <Button color="primary" type="submit" disabled={loadingSubmit}>
              {!loadingSubmit ? (
                'ATUALIZAR DADOS'
              ) : (
                <Spinner type="ThreeDots" />
              )}
            </Button>
          </Form>
        </Content>
      </CardAvatar>
      {hasAvaliador && !loading && (
        <WidgetContent>
          <Widget
            title="Chamadas com avaliação pendente"
            type="condensed"
            icon={<FiVideo />}
            showLink={false}
            active={false}
            inverted
            loading={false}
            handleClick={() => navigate('/chamadas/aprovacao')}
            data={workflow.chamadas}
            color="warn"
          />
          <Widget
            title="Spots com avaliação pendente"
            type="condensed"
            icon={<FiMusic />}
            showLink={false}
            active={false}
            inverted
            loading={false}
            handleClick={() => navigate('/spotradio/aprovacao')}
            data={workflow.spotradio}
            color="warn"
          />
          <Widget
            title="Roteiros com avaliação pendente"
            type="condensed"
            icon={<FiBookOpen />}
            showLink={false}
            active={false}
            inverted
            loading={false}
            handleClick={() => navigate('/roteiropromo/aprovacao')}
            data={workflow.roteiropromo}
            color="warn"
          />
        </WidgetContent>
      )}
    </Container>
  );
}
